/* eslint-disable no-nested-ternary */
import React, { lazy, Suspense, useEffect, useState } from 'react';
// @mui
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
// import { ErrorBoundary } from 'react-error-boundary';
import { fetchAndActivate, getValue } from "firebase/remote-config";
// mui theme
import * as Sentry from "@sentry/react";
import { createRoutesFromChildren, matchRoutes, useLocation, useSearchParams } from "react-router-dom";
import { useNavigationType } from "react-router";
import ThemeProvider from './theme';
// contexts
import { AuthProvider } from './contexts/AuthContext';
import { IdleProvider } from './contexts/IdleProvider';
// hooks
import { useServiceWorker } from './hooks/useServiceWorker.ts';
import useResponsive from './hooks/useResponsive';
import ErrorFallback from './components/error/ErrorFallback';
// Error Boundary
// Loading Spinner component
import LoadingSpinner from './components/micro/LoadingSpinner';
import { remoteConfig } from './configs/firesBaseConfig';
import SplashScreen from './components/splash/SplashScreen';
import useLocalStorage from './hooks/useLocalStorage';
import { ServiceWorkerUpdateDialog } from './components/others/ServiceWorkerUpdateDialog';
import ConnectionStatusSnackbar from './components/others/ConnectionStatusSnackbar';
// Offline sync service
import offlineSyncService from './services/offlineSyncService';

// Lazy-loaded components
const Router = lazy(() => import('./routes'));
const ScrollToTop = lazy(() => import('./components/micro/ScrollToTop'));
const NotistackProvider = lazy(() => import('./components/others/NotistackProvider'));
const Settings = lazy(() => import('./components/settings'));


// javascript-time-ago initialized
TimeAgo.addDefaultLocale(en);

// -----------------------------------------------

export default function App() {
    // Calling below hook registers service worker
    // eslint-disable-next-line no-unused-vars
    // const { updateAvailable } = useServiceWorker();
    const [searchParams] = useSearchParams();
    const [isSplashVisible, setIsSplashVisible] = useState(true);
    const isDesktop = useResponsive('up', 'lg');
    const [redirectURL] = useState(searchParams.get("redirect"));
    const [splashData, setSplashData] = useState({
        splash_icon: '', text_message: '', splash_bg: '',
    });
    const [showSplash, setShowSplash] = useLocalStorage('splashShown');

    const [dialog, setDialog] = useState(false);
    const { waitingWorker, showReload, reloadPage } = useServiceWorker();
    // decides when to show the toast
    useEffect(() => {
        if (showReload && waitingWorker) {
            setDialog(true)
            setTimeout(() => {
                reloadPage();
            }, 2000);
        } else setDialog(false);
    }, [waitingWorker, showReload, reloadPage]);

    useEffect(() => {
        if (redirectURL && redirectURL !== undefined) {
            window.open(redirectURL, '_self');
        }
        // Sentry initialization
        if (!window.__SENTRY_INITIALIZED__) {
            Sentry.init({
                dsn: process.env.REACT_APP_SENTRY_DSN,
                integrations: [
                    Sentry.reactRouterV6BrowserTracingIntegration({
                        useEffect,
                        useLocation,
                        useNavigationType,
                        createRoutesFromChildren,
                        matchRoutes,
                    }),
                    Sentry.replayIntegration(),
                ],

                environment: process.env.REACT_APP_ENV,
                tracesSampleRate: 0.05, // Set to 5% for free account
                tracePropagationTargets: [process.env.REACT_APP_HOST],
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0,
                maxBreadcrumbs: 50, // Set a rate limit for breadcrumbs
            });
            window.__SENTRY_INITIALIZED__ = true;
        }

        // Initialize offline sync service
        // Attempt initial sync if online
        if (navigator.onLine) {
            offlineSyncService.syncPendingResolutions().catch(err => {
                console.error('Initial sync failed:', err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'RELOAD') {
            console.log('Reloading page due to service worker update...');
            // Reload the page
            window.location.reload();
        }
    });



    const getSplashData = () => {
        fetchAndActivate(remoteConfig)
            .then(() => {
                const configValue = getValue(remoteConfig, "custom_splash_screen").asString();
                const splashConfig = JSON.parse(configValue);
                if (splashConfig) {
                    setSplashData(splashConfig);
                }
            })
            .catch((err) => {
                console.error("Error fetching or parsing remote config value:", err);
            });
    };

    // Check if splash was shown before
    function handleSplashTimeout(currentTime, setIsSplashVisible, setShowSplash) {
        setTimeout(() => {
            setIsSplashVisible(false);
            // Set the shown time to 1 hour in the future (3600000 milliseconds)
            setShowSplash(currentTime + 3600000);
        }, 3000);
    }

    useEffect(() => {
        getSplashData();
        const currentTime = Date.now();
        // Check if it's been more than one hour since last splash
        if (showSplash === undefined) {
            // Splash screen has never been shown
            handleSplashTimeout(currentTime, setIsSplashVisible, setShowSplash);
        } else if (currentTime > showSplash) {
            // More than one hour since the last splash screen
            handleSplashTimeout(currentTime, setIsSplashVisible, setShowSplash);
        } else {
            // Splash screen is still within the one-hour limit
            setIsSplashVisible(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fallbackRender = ({ error, resetError }) => {
        console.error("Error Boundary Caught an Error on Console: ", error);
        // Check if the error is a ChunkLoadError
        if (error.name === 'ChunkLoadError' || error.message.includes('ChunkLoadError')) {
            // Reload the page after a short delay
            setTimeout(() => {
                window.location.reload();
            }, 1000);
            // TODO: On CHunkLoadError, how can we skip the delay and reload the page immediately?
            // You can return a loading spinner or null while the page reloads
            return <LoadingSpinner />;
        }

        // For other errors, use the existing ErrorFallback
        return (
            <ErrorFallback
                resetError={resetError}
            />
        );
    };


    if (redirectURL && redirectURL !== undefined) {
        return <LoadingSpinner />
    }


    return (
        <div>
            <ThemeProvider>
                <ServiceWorkerUpdateDialog dialog={dialog} />
                <Sentry.ErrorBoundary fallback={fallbackRender}>
                    {isSplashVisible && !isDesktop && (
                        <SplashScreen
                            messageData={splashData.text_message}
                            imageURL={splashData.splash_icon}
                            bgMobileSplash={splashData.splash_bg}
                        />
                    )}
                    {!isSplashVisible && (
                        <NotistackProvider>
                            <ConnectionStatusSnackbar />
                            <AuthProvider>
                                <IdleProvider>
                                    <Settings />
                                    <ScrollToTop />
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <Router />
                                        </Suspense>
                                    </LocalizationProvider>
                                </IdleProvider>
                            </AuthProvider>
                        </NotistackProvider>
                    )}
                </Sentry.ErrorBoundary>
            </ThemeProvider>
        </div>
    );
}